import React, { useCallback } from 'react';
import styled from 'styled-components';
import {
    Group,
    Text,
    Image,
    color,
    FolderMusic,
    IconButton,
} from '@landr/maestro';
import { plural, t } from '@lingui/macro';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { Collection, Sample } from 'types';
import { useMutation, useApolloClient, gql } from '@apollo/client';
import {
    AddSamplesToUserCollectionMutation,
    UserCollectionQuery,
} from 'apollo/userCollection/userCollection.gql';
import { QueryConfig } from 'apollo/userCollection/queryConfig';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import { evictCacheByKeys } from 'helpers';
import {
    AddedMultipleSamplesToCollectionError,
    AddedMultipleSamplesToCollectionSuccess,
    AddedSingleSampleToCollectionError,
    AddedSingleSampleToCollectionSuccess,
} from 'contexts/NotificationsContext/notifications';
import { getImageUrl } from '../../helpers';

export interface CollectionsListItemProps {
    collectionId: string;
    item: Collection;
    sampleIds?: string[];
    hide: () => void;
}

const PackIcon = styled(Image)`
    position: relative;
    width: 36px;
    height: 36px;
    border-radius: 2px;
    color: ${color('text.base')};
    ${({ theme }) => theme.breakpoint('md')`
        width: 28px;
        height: 28px
    `}
`;

const NameAndMobileDetailsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-content: center;
    flex-wrap: wrap;
    ${({ theme }) => theme.breakpoint('sm')`
        font-weight: 400;
    `}
`;

const StyledTextName = styled(Text)`
    line-height: 19px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 232px;
    color: ${color('text.base')};
`;

const StyledTextCount = styled(Text)`
    line-height: 16px;
`;

const useGetSampleFragment = (sampleId?: string) => {
    const apolloClient = useApolloClient();

    if (!sampleId) {
        return undefined;
    }

    const sampleFragment: Partial<Sample> | null = apolloClient.readFragment({
        id: `Sample:{"guid":"${sampleId}"}`,
        fragment: gql`
            fragment SampleFromGuid on Sample {
                guid
                name
            }
        `,
    });

    return sampleFragment?.name;
};

export const CollectionsListItem: React.FC<CollectionsListItemProps> = ({
    item,
    sampleIds,
    hide,
}) => {
    const { clearSamplesSelection } = useSelectedSamplesContext();
    const { createNotification } = useNotificationsContext();

    const sampleName = useGetSampleFragment(sampleIds && sampleIds[0]);

    const [requestAddSamplesToUserCollection] = useMutation(
        AddSamplesToUserCollectionMutation,
        {
            variables: { collectionGuid: item.guid, sampleIds },
            onCompleted: () => {
                if (!sampleIds) {
                    return;
                }

                if (sampleIds.length > 1) {
                    createNotification(
                        new AddedMultipleSamplesToCollectionSuccess(
                            item.name,
                            sampleIds.length,
                        ),
                    );
                } else {
                    createNotification(
                        new AddedSingleSampleToCollectionSuccess(
                            item.name,
                            sampleName,
                        ),
                    );
                }
            },
            onError: () => {
                if (!sampleIds) {
                    return;
                }

                if (sampleIds.length > 1) {
                    createNotification(
                        new AddedMultipleSamplesToCollectionError(
                            item.name,
                            sampleIds.length,
                        ),
                    );
                } else {
                    createNotification(
                        new AddedSingleSampleToCollectionError(
                            item.name,
                            sampleName,
                        ),
                    );
                }
            },
            update: (cache) => {
                evictCacheByKeys(cache, [
                    item?.guid,
                    QueryConfig.pathToItems,
                    'userCollectionsFeed',
                ]);
            },
            // refetch is needed to get new generated artwork
            refetchQueries: [
                {
                    query: UserCollectionQuery,
                    variables: { collectionGuid: item.guid },
                    fetchPolicy: 'network-only',
                },
            ],
        },
    );

    const onCardClick = useCallback(async () => {
        if (!sampleIds) {
            return;
        }

        await requestAddSamplesToUserCollection();

        clearSamplesSelection();
        hide();
    }, [
        sampleIds,
        clearSamplesSelection,
        hide,
        requestAddSamplesToUserCollection,
    ]);

    return (
        <Group
            alignItems="center"
            pt="sm"
            pb="sm"
            pl="lg"
            pr="lg"
            onClick={onCardClick}
        >
            <Group.Item>
                <PackIcon
                    src={getImageUrl({ imageUrl: item.artworkUrl, width: 36 })}
                    loading="lazy"
                    fallback={() => (
                        <IconButton
                            icon={FolderMusic}
                            kind="link"
                            label={t`Artwork`}
                            isDisabled={true}
                            size="lg"
                        />
                    )}
                />
            </Group.Item>
            <Group.Item>
                <NameAndMobileDetailsWrapper>
                    <StyledTextName size="md" mb="0" fontWeight="normal">
                        {item.name}
                    </StyledTextName>
                    <StyledTextCount contrast="low" size="sm">
                        {plural(item.sampleCount, {
                            one: '# sample',
                            other: '# samples',
                        })}
                    </StyledTextCount>
                </NameAndMobileDetailsWrapper>
            </Group.Item>
        </Group>
    );
};
